import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { useTagContext } from 'lib/context/TagContext/TagContext';

import Button from 'components/shared/Button/Button';
import ErrorBoundary from 'components/shared/Error/Boundary';
import Icon from 'components/shared/Icon/Icon';

import Modal from '../../../Modal/Modal';
import { updateTagLinks } from '../../lib/updateTagLinks';

import Reorder from './components/Reorder';

/**
 * @param {import('./lib/propTypes').TagsReorderWrapperProps} props
 */

const TagsReorderWrapper = (props) => {
  const {
    openReorderModal = false,
    handleClose = () => {},
    refId = null,
    fromTable = false,
    refTable,
    kind
  } = props;

  const { importantTags, setImportantTags, regularTags, setRegularTags, setFormattedTags } =
    useTagContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const [updatedImportantTagLinks, setUpdatedImportantTagLinks] = useState([]);
  const [updatedRegularTagLinks, setUpdatedRegularTagLinks] = useState([]);

  const [lists, setLists] = useState({
    important: importantTags || [],
    regular: regularTags || []
  });

  useEffect(() => {
    setLists({ important: importantTags || [], regular: regularTags || [] });
  }, [importantTags, regularTags]);

  const handleDragEnd = useCallback((result) => {
    const { source, destination } = result;
    if (!destination) return;

    const src = source.droppableId;
    const dst = destination.droppableId;
    const sameList = src === dst;
    if (sameList && source.index === destination.index) return;

    setLists((prev) => {
      const imp = [...prev.important];
      const reg = [...prev.regular];

      if (src === 'importantTags' && dst === 'importantTags') {
        const [m] = imp.splice(source.index, 1);
        imp?.splice(destination.index, 0, m);
      } else if (src === 'regularTags' && dst === 'regularTags') {
        const [m] = reg.splice(source.index, 1);
        reg?.splice(destination.index, 0, m);
      } else if (src === 'importantTags' && dst === 'regularTags') {
        const [m] = imp.splice(source.index, 1);
        m.important = false;
        reg?.splice(destination.index, 0, m);
      } else if (src === 'regularTags' && dst === 'importantTags') {
        const [m] = reg.splice(source.index, 1);
        m.important = true;
        imp?.splice(destination.index, 0, m);
      }

      setUpdatedImportantTagLinks(
        imp?.map((tag, i) => ({ id: tag.id, fields: { position: i + 1, important: true } }))
      );
      setUpdatedRegularTagLinks(
        reg?.map((tag, i) => ({ id: tag.id, fields: { position: i + 1, important: false } }))
      );
      return { important: imp, regular: reg };
    });
  }, []);

  const handleSave = async () => {
    const combinedTags = [...updatedImportantTagLinks, ...updatedRegularTagLinks];
    await updateTagLinks({
      navigate,
      queryClient,
      id: fromTable ? refId : id,
      refTable,
      fromTable: !fromTable,
      mode: 'update',
      tags: combinedTags,
      isInvalidate: true,
      query: {
        multiple: true,
        keys: [
          ['tags', kind],
          ...(refTable === 'user' && !fromTable ? [['patient', Number(id)], ['patients']] : [])
        ]
      }
    });
    setImportantTags(lists?.important);
    setRegularTags(lists?.regular);
    setFormattedTags([...lists.important, ...lists.regular]);
    handleClose();
  };

  const ModalHeader = () => {
    return (
      <div className="flex items-center gap-[6px]">
        <Icon icon="arrow-swap-down-mono" color="primary" />
        <h6 className="text-sm font-500 text-primary-900">Re-order Tags</h6>
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <Modal
        isOpen={openReorderModal}
        customHeader={<ModalHeader />}
        handleClose={handleClose}
        className="!max-h-[400px]"
        bodyClassName="bg-primary-10"
        footer={
          <div className="flex w-full items-center justify-between">
            <Button text="Close" onClick={handleClose} variant="tertiary" size="small" />
            <Button text="Save" onClick={handleSave} size="small" />
          </div>
        }>
        <Reorder
          importantTags={lists?.important}
          regularTags={lists?.regular}
          onDragEnd={handleDragEnd}
        />
      </Modal>
    </ErrorBoundary>
  );
};

export default TagsReorderWrapper;
